import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { performCookieDiagnostic } from '@/utils/detectThirdPartyCookies';
import {
    detectKeycloakLoop,
    startKeycloakLoopMonitoring,
    breakKeycloakLoop,
    clearRedirectAttempts
} from '@/utils/detectKeycloakLoop';
import {
    getKeycloakDomain,
    getBrowserSpecificInstructions,

} from '@/utils/browserCookieConfig';
import { cookieDetectionUtils } from '@/utils/advancedCookieDetector';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/atoms/texts/label';

export const CookieCompatibilityProvider = ({ children }) => {
    const [t] = useTranslation("global");
    const [isThirdPartyCookiesEnabled, setIsThirdPartyCookiesEnabled] = useState(true);
    const [isChecking, setIsChecking] = useState(true);
    const [error, setError] = useState(null);
    const [loopDetected, setLoopDetected] = useState(false);
    const [diagnostic, setDiagnostic] = useState(null);
    const [browserInstructions, setBrowserInstructions] = useState(null);

    // Refs pour éviter les dépendances circulaires
    const checkingInProgressRef = useRef(false);
    const cleanupFunctionsRef = useRef([]);
    const handleKeycloakLoopRef = useRef(null);
    const handleKeycloakSecurityErrorRef = useRef(null);
    const performCompatibilityCheckRef = useRef(null);

    // Fonction pour gérer la détection de boucle Keycloak
    const handleKeycloakLoop = useCallback((event) => {
        console.log('🔍 [CookieCompatibility] handleKeycloakLoop appelé - recréé ?');
        console.error('🚨 [CookieCompatibility] Boucle Keycloak détectée!', event.detail);
        setLoopDetected(true);
        setError('Boucle d\'authentification détectée. Ceci est généralement causé par des cookies tiers bloqués.');

        // Attendre un peu puis tenter de sortir de la boucle
        setTimeout(() => {
            if (confirm('Une boucle d\'authentification a été détectée. Voulez-vous tenter de la résoudre en nettoyant les données d\'authentification ?')) {
                breakKeycloakLoop();
            }
        }, 2000);
    }, []);

    // Mettre à jour la ref à chaque fois que la fonction change
    useEffect(() => {
        handleKeycloakLoopRef.current = handleKeycloakLoop;
    }, [handleKeycloakLoop]);

    // Fonction pour gérer les erreurs de sécurité Keycloak
    const handleKeycloakSecurityError = useCallback((event) => {
        console.log('🔍 [CookieCompatibility] handleKeycloakSecurityError appelé - recréé ?');
        console.error('🚨 [CookieCompatibility] Erreur de sécurité Keycloak détectée!', event.detail);

        setIsThirdPartyCookiesEnabled(false);
        setIsChecking(false); // Arrêter le spinner immédiatement

        // Message d'erreur plus détaillé
        const errorContext = event.detail?.context || 'opération Keycloak';
        setError(t('error.cookie.keycloakSecurityError') + ` (${errorContext})`);

        // Mettre à jour le diagnostic
        setDiagnostic(prev => ({
            ...prev,
            keycloakSecurityError: true,
            errorDetails: event.detail,
            thirdPartyCookiesEnabled: false,
            firstPartyCookiesEnabled: false,
            errorContext: event.detail?.context
        }));
    }, [t]);

    // Mettre à jour la ref à chaque fois que la fonction change
    useEffect(() => {
        handleKeycloakSecurityErrorRef.current = handleKeycloakSecurityError;
    }, [handleKeycloakSecurityError]);

    // Fonction pour effectuer un test complet
    const performCompatibilityCheck = useCallback(async (isBackgroundCheck = false) => {
        console.log('🔍 [CookieCompatibility] performCompatibilityCheck appelé - recréé ?');
        console.log('🔄 [CookieCompatibility] Démarrage du contrôle de compatibilité');

        // Seulement bloquer l'UI lors du chargement initial
        if (!isBackgroundCheck) {
            setIsChecking(true);
        }

        setError(null);

        try {
            // 1. Test avec le détecteur de cookies amélioré
            // console.log('🚀 [CookieCompatibility] Test des cookies...');
            const cookieResult = await cookieDetectionUtils.immediateTest();

            // 2. Effectuer un diagnostic complet classique
            const fullDiagnostic = await performCookieDiagnostic();

            // 3. Combiner les résultats pour plus de précision
            const enhancedDiagnostic = {
                ...fullDiagnostic,
                thirdPartyCookiesEnabled: cookieResult || fullDiagnostic.thirdPartyCookiesEnabled,
                testMethod: cookieResult ? 'advanced-detector' : 'classic',
                cookieTestResult: cookieResult
            };

            setDiagnostic(enhancedDiagnostic);

            // console.log('🔄 [CookieCompatibility] Diagnostic amélioré:', enhancedDiagnostic);

            // Vérifier s'il y a des boucles de redirection
            const loopStatus = detectKeycloakLoop();
            if (loopStatus.isInLoop) {
                console.warn('🔄 [CookieCompatibility] Vraie boucle de redirection détectée:', loopStatus.reason);
                setLoopDetected(true);
                setError(t('error.cookie.authLoopWithReason', { reason: loopStatus.reason }));
            } else if (loopStatus.attemptCount > 0) {
                console.info('ℹ️ [CookieCompatibility] Redirections Keycloak normales détectées:', loopStatus.reason);
            }

            // Logique de décision basée sur le diagnostic amélioré ET la boucle Keycloak
            // Seules les VRAIES boucles (redirections rapides) indiquent un problème de cookies
            const cookiesCompatible = enhancedDiagnostic.thirdPartyCookiesEnabled &&
                enhancedDiagnostic.firstPartyCookiesEnabled;

            // Une vraie boucle indique un problème réel de cookies
            const hasRealCookieProblem = loopStatus.isInLoop;

            // console.log(`🎯 [CookieCompatibility] Résultat final - Cookies Compatible: ${cookiesCompatible}, Vraie boucle détectée: ${loopStatus.isInLoop}, Problème réel: ${hasRealCookieProblem} (méthode: ${enhancedDiagnostic.testMethod})`);

            // Si on a une vraie boucle OU si les cookies ne fonctionnent pas, on considère que c'est incompatible
            const finalCompatibility = cookiesCompatible && !hasRealCookieProblem;

            if (!finalCompatibility) {
                let errorMsg = t('error.cookie.problemDetected');

                if (hasRealCookieProblem) {
                    errorMsg += t('error.cookie.thirdPartyBlockedKeycloak');
                } else if (!enhancedDiagnostic.firstPartyCookiesEnabled) {
                    errorMsg += t('error.cookie.cookiesCompletelyDisabled');
                } else if (!enhancedDiagnostic.thirdPartyCookiesEnabled) {
                    errorMsg += t('error.cookie.thirdPartyBlockedAuth');
                }

                console.warn('⚠️ [CookieCompatibility] ' + errorMsg);
                setError(errorMsg);
            }

            setIsThirdPartyCookiesEnabled(finalCompatibility);

            return fullDiagnostic;

        } catch (err) {
            console.error('❌ [CookieCompatibility] Erreur lors du contrôle de compatibilité:', err);
            setError(t('error.cookie.errorCookieCheck'));
            setIsThirdPartyCookiesEnabled(false);
            throw err;
        } finally {
            setIsChecking(false);
            checkingInProgressRef.current = false;
        }
    }, [t]);

    // Mettre à jour la ref à chaque fois que la fonction change
    useEffect(() => {
        performCompatibilityCheckRef.current = performCompatibilityCheck;
    }, [performCompatibilityCheck]);

    // Effet pour récupérer les instructions spécifiques au navigateur
    useEffect(() => {
        const keycloakDomain = getKeycloakDomain();
        const instructions = getBrowserSpecificInstructions(keycloakDomain, t);
        setBrowserInstructions(instructions);
        // console.log('🛠️ [CookieCompatibility] Instructions navigateur chargées:', instructions);
    }, [t]); // Ajouter t comme dépendance pour re-charger les instructions si la langue change

    // Effet principal pour initialiser la surveillance (uniquement au montage)
    useEffect(() => {
        console.log('🚀 [CookieCompatibility] Initialisation du provider - DEVRAIT SE PRODUIRE UNE SEULE FOIS');

        let isActive = true;

        // Fonction de nettoyage pour éviter les fuites mémoire
        const addCleanup = (cleanupFn) => {
            cleanupFunctionsRef.current.push(cleanupFn);
        };

        // Wrapper pour utiliser les refs
        const handleKeycloakLoopWrapper = (event) => {
            if (handleKeycloakLoopRef.current) {
                handleKeycloakLoopRef.current(event);
            }
        };

        const handleKeycloakSecurityErrorWrapper = (event) => {
            if (handleKeycloakSecurityErrorRef.current) {
                handleKeycloakSecurityErrorRef.current(event);
            }
        };

        // ⚠️ IMPORTANT: Écouter l'événement de sécurité Keycloak EN PREMIER
        // Car il peut être émis AVANT le premier contrôle de compatibilité
        window.addEventListener('keycloakSecurityError', handleKeycloakSecurityErrorWrapper);
        addCleanup(() => window.removeEventListener('keycloakSecurityError', handleKeycloakSecurityErrorWrapper));

        // Premier contrôle immédiat (après avoir installé l'écouteur)
        if (performCompatibilityCheckRef.current) {
            performCompatibilityCheckRef.current(false).catch(console.error);
        }

        // Démarrer la surveillance des boucles Keycloak
        const stopLoopMonitoring = startKeycloakLoopMonitoring();
        addCleanup(stopLoopMonitoring);

        // Écouter les événements de boucle détectée
        window.addEventListener('keycloakLoopDetected', handleKeycloakLoopWrapper);
        addCleanup(() => window.removeEventListener('keycloakLoopDetected', handleKeycloakLoopWrapper));

        // Surveillance périodique moins fréquente
        const watchInterval = setInterval(() => {
            if (isActive && !checkingInProgressRef.current && performCompatibilityCheckRef.current) {
                // console.log('👀 [CookieCompatibility] Contrôle périodique des cookies');
                performCompatibilityCheckRef.current(true).catch(console.error);
            }
        }, 60000);
        addCleanup(() => clearInterval(watchInterval));

        // Surveillance lors des changements de visibilité de la page
        const handleVisibilityChange = () => {
            if (isActive && !document.hidden && !checkingInProgressRef.current && performCompatibilityCheckRef.current) {
                // console.log('👁️ [CookieCompatibility] Page redevenue visible - vérification des cookies');
                setTimeout(() => {
                    if (isActive && performCompatibilityCheckRef.current) {
                        performCompatibilityCheckRef.current(true).catch(console.error);
                    }
                }, 2000);
            }
        };

        // Surveillance lors du regain de focus
        const handleFocus = () => {
            if (isActive && !checkingInProgressRef.current && performCompatibilityCheckRef.current) {
                // console.log('🎯 [CookieCompatibility] Fenêtre focalisée - vérification des cookies');
                setTimeout(() => {
                    if (isActive && performCompatibilityCheckRef.current) {
                        performCompatibilityCheckRef.current(true).catch(console.error);
                    }
                }, 2000);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);

        addCleanup(() => document.removeEventListener('visibilitychange', handleVisibilityChange));
        addCleanup(() => window.removeEventListener('focus', handleFocus));

        return () => {
            console.log('🧹 [CookieCompatibility] Nettoyage du provider - DEVRAIT SE PRODUIRE UNIQUEMENT AU DÉMONTAGE');
            isActive = false;

            // Exécuter toutes les fonctions de nettoyage
            cleanupFunctionsRef.current.forEach(cleanup => {
                try {
                    cleanup();
                } catch (err) {
                    console.error('❌ [CookieCompatibility] Erreur lors du nettoyage:', err);
                }
            });
            cleanupFunctionsRef.current = [];
        };
    }, []); //  Plus de dépendances - s'exécute uniquement au montage/démontage

    console.log('isChecking ' + isChecking + ' - isThirdPartyCookiesEnabled ' + isThirdPartyCookiesEnabled);

    // On bloque l'application SEULEMENT si les cookies tiers sont réellement bloqués
    // Une boucle Keycloak seule n'est pas suffisante pour bloquer si les cookies fonctionnent
    if (!isChecking && !isThirdPartyCookiesEnabled) {
        return (
            <div className="fixed top-0 left-0 w-screen h-screen bg-gray-50 flex items-center justify-center z-[9999] p-4">
                <div className="max-w-4xl bg-white rounded-lg shadow-xl overflow-hidden">
                    {/* Header */}
                    <div className="bg-primary text-white p-6 text-center">
                        <div className="text-5xl mb-3">
                            🍪
                        </div>
                        <Label
                            message="error.cookie.thirdPartyBlocked"
                            size="h2"
                            color="white"
                            className="block"
                        />
                    </div>

                    {/* Content */}
                    <div className="p-6 max-h-[80vh] overflow-auto">
                        <Label
                            message="error.cookie.blockedDescription"
                            size="body"
                            className="mb-5 block leading-relaxed text-center"
                        />



                        {/* Instructions de configuration intégrées */}
                        {browserInstructions && (
                            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 mb-6">
                                <div className="text-center mb-4">
                                    <div className="text-3xl mb-2">{browserInstructions.instructions.icon || '🛠️'}</div>
                                    <Label
                                        size="h4"
                                        color="primary"
                                        className="block mb-2"
                                    >
                                        {t('error.cookie.configurationFor', { browserName: browserInstructions.instructions.title })}
                                    </Label>
                                    <p className="text-sm text-blue-700 mb-4">
                                        <Label message="error.cookie.domainToAllow" />: <code className="bg-blue-100 px-2 py-1 rounded font-mono">{browserInstructions.domain}</code>
                                    </p>
                                </div>

                                {/* Actions rapides
                                <div className="flex flex-wrap gap-3 justify-center mb-4">
                                    <Button
                                        onClick={async () => {
                                            const success = await copyInstructionsToClipboard(browserInstructions.domain);
                                            if (success) {
                                                // Optionnel: ajouter un feedback visuel
                                                alert(t('error.cookie.instructionsCopiedAlert'));
                                            } else {
                                                alert(t('error.cookie.copyFailure'));
                                            }
                                        }}
                                        variant="default"
                                        size="default"
                                        className="flex items-center gap-2"
                                        text="error.cookie.copyInstructions"
                                    />
                                </div> */}

                                {/* Instructions étape par étape */}
                                <div className="text-left">
                                    <Label message="error.cookie.detailedInstructions" size="h5" className="block mb-3" />
                                    <ol className="pl-6 space-y-2 text-sm">
                                        {browserInstructions.instructions.steps.map((step, index) => (
                                            <li key={index} className="leading-relaxed">
                                                <strong className="text-primary">{t('error.cookie.step', { stepNumber: index + 1 })}:</strong> {step}
                                            </li>
                                        ))}
                                    </ol>
                                </div>

                                {/* URLs d'aide */}
                                {browserInstructions.instructions.helpUrl && (
                                    <div className="mt-4 pt-4 border-t border-blue-200">
                                        <p className="text-xs text-blue-600">
                                            <a
                                                href={browserInstructions.instructions.helpUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="underline hover:text-blue-800"
                                            >
                                                <Label message="error.cookie.officialGuide" />
                                            </a>
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Boutons d'actions */}
                        <div className="flex flex-wrap gap-3 justify-center">
                            <Button
                                onClick={async () => {
                                    // console.log('🔄 Nettoyage et nouvelle vérification...');
                                    clearRedirectAttempts();
                                    await cookieDetectionUtils.forceRefresh();
                                    await performCompatibilityCheck(false);
                                }}
                                variant="outline"
                                size="default"
                                text="error.cookie.cleanAndRetest"
                            />

                            <Button
                                onClick={() => {
                                    // Nettoyer tout avant de recharger
                                    clearRedirectAttempts();
                                    window.location.reload();
                                }}
                                variant="default"
                                size="default"
                                text="error.cookie.reloadPage"
                            />
                        </div>


                    </div>
                </div>
            </div>
        );
    }

    // Pendant le chargement initial, afficher un spinner
    if (isChecking) {
        return (

            <div>
                <div class="loading-container">
                    <img src="../src/assets/images/logoWithBrand.png" alt="Synapse Logo"
                        style={{ height: '350px', width: 'auto', objectFit: 'contain' }} />
                    <div class="loading-content">
                        <p class="text">Welcome to</p>
                        <h1 class="loading-title text">Kubrik 21</h1>
                        <Label message="error.cookie.compatibilityCheck" />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            {children}
        </>
    );
};
